import React from "react";
import ProfileCard from "../../components/instructor/ProfileCard";
import InstructorLayout from "../../layouts/InstructorLayout";
import {
  BookOpen,
  PlayCircle,
  Trophy,
  Users,
} from "../../components/instructor/icons";
import { useGetMyAssignedCoursesQuery } from "../../services/instructor/instructor";
import AssignedCourses from "../../components/instructor/AssignedCourses";

const MyCourses = () => {
  const {
    data: assignedCoursesResponse,
    isLoading,
    isError,
    error,
  } = useGetMyAssignedCoursesQuery({
    page: 1,
    limit: 10,
  });

  const assignedCourses = Array.isArray(assignedCoursesResponse?.data)
    ? assignedCoursesResponse.data.filter((course) => course?._id)
    : [];

  const totalCourses =
    assignedCoursesResponse?.pagination?.totalCourses ?? assignedCourses.length;

  const emptyMessage =
    assignedCoursesResponse?.message ||
    error?.data?.message ||
    "No courses assigned to this instructor";

  const hasNoCourses = !isLoading && !isError && assignedCourses.length === 0;

  const stats = [
    {
      title: "In-Process",
      value: hasNoCourses ? 0 : 2,
      icon: <PlayCircle />,
      bg: "bg-[#FFEEE8]",
    },
    {
      title: "Total Courses",
      value: totalCourses,
      icon: <BookOpen />,
      bg: "bg-[#E1F7E3]",
    },
    {
      title: "Students",
      value: hasNoCourses ? 0 : 20,
      icon: <Users />,
      bg: "bg-[#FFF0F0]",
    },
    {
      title: "Completed Courses",
      value: hasNoCourses ? 0 : 6,
      icon: <Trophy />,
      bg: "bg-[#E1F7E3]",
    },
  ];

  return (
    <InstructorLayout>
      <div className="pt-6 px-8 2xl:pr-[60px] 2xl:pl-9">
        <div className="mt-5 grid grid-cols-1 gap-4 min-[480px]:grid-cols-2 sm:mt-[30px] sm:gap-5 xl:grid-cols-4 lg:gap-6 2xl:gap-12">
          {stats.map((item, index) => (
            <ProfileCard
              key={index}
              icon={item.icon}
              value={item.value}
              title={item.title}
              bg={item.bg}
            />
          ))}
        </div>

        <div className="mt-6">
          {isLoading ? (
            <div className="flex min-h-[240px] items-center justify-center rounded-lg border border-[#E5E7EB] bg-white px-6 py-10">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#089BC4] border-t-transparent" />
            </div>
          ) : isError ? (
            <div className="rounded-lg border border-[#FECACA] bg-[#FEF2F2] px-6 py-10 text-center">
              <p className="text-base font-semibold text-[#991B1B]">
                Failed to load courses
              </p>
              <p className="mt-2 text-sm text-[#B91C1C]">
                {error?.data?.message || "Please try again later."}
              </p>
            </div>
          ) : assignedCourses.length > 0 ? (
            <AssignedCourses courses={assignedCourses} />
          ) : (
            <div className="w-full overflow-hidden bg-white px-4 pb-8 pt-4 shadow-[0_-1px_3.8px_0_#00000040] sm:px-6 md:px-7 md:pb-10">
              <h2 className="text-lg font-semibold leading-10 tracking-[-1%] text-[#1D2026] sm:text-xl sm:leading-[48px]">
                My Courses
              </h2>

              <div className="mt-2 flex min-h-[280px] flex-col items-center justify-center rounded-2xl border border-dashed border-[#089BC4]/40 bg-[#089BC40A] px-6 py-12 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#089BC41A] text-[#089BC4]">
                  <BookOpen />
                </div>

                <h3 className="text-lg font-semibold text-[#1D2026] sm:text-xl">
                  No courses assigned yet
                </h3>

                <p className="mt-3 max-w-md text-sm leading-6 text-[#6E7485] sm:text-base">
                  {emptyMessage}
                </p>

                <p className="mt-2 max-w-md text-xs leading-5 text-[#9CA3AF] sm:text-sm">
                  Once admin assigns courses to you, they will appear here.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </InstructorLayout>
  );
};

export default MyCourses;
